import { formatShortDate } from "common/components/localization";
import { getNowTimeZoneAgnostic } from "date-time/support";
import * as React from "react";
import type { Subtract } from "utility-types";
import type { CalendarScheduleProps, Event } from "webapp/ui/views/calendar-schedule/calendar-schedule";
import CalendarSchedule from "webapp/ui/views/calendar-schedule/calendar-schedule";

interface Handled {
    readonly header: string;
    readonly viewDate: Date;

    readonly onDateClick?: (date: Date) => void;
    readonly onHeaderClick?: () => void;
}

type Props = Subtract<CalendarScheduleProps<unknown>, Handled>;

const EventPicker: React.FunctionComponent<Props> = p => {
    const { onSelectionChange } = p;
    const [date, setDate] = React.useState<Date>(new Date(p.selection?.start ?? getNowTimeZoneAgnostic()));
    const [selection, setSelection] = React.useState<Event | undefined>(p.selection);

    const header = formatShortDate(date, false) ?? "";

    const onChange = React.useCallback(
        (newSelection: Event) => {
            let s = new Date(Math.floor(newSelection.start.getTime() / 9e5) * 9e5);
            const e = new Date(Math.ceil(newSelection.end.getTime() / 9e5) * 9e5);

            if (s >= e) return;

            if (s.getUTCDay() !== date.getUTCDay()) {
                s = new Date(date);
                s.setUTCHours(0, 0, 0, 0);
            }

            if (s.getUTCDay() !== e.getUTCDay()) {
                e.setUTCHours(0, 0, 0, 0);
            }

            if (
                selection !== undefined &&
                selection.start.getTime() === s.getTime() &&
                selection.end.getTime() === e.getTime()
            )
                return;

            newSelection = {
                ...newSelection,
                start: s,
                end: e,
            };
            setSelection(newSelection);
            onSelectionChange?.(newSelection);
        },
        [date, onSelectionChange, selection]
    );

    return (
        <CalendarSchedule
            {...p}
            header={header}
            selection={selection}
            viewDate={date}
            onDateClick={setDate}
            onSelectionChange={onChange}
        />
    );
};

export default EventPicker;
